import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import styles from './Camera.module.scss'
import Webcam from 'react-webcam';
import ReactModal from 'react-modal';
import CheckIcon from '../../../assets/svg/check.svg'
import CloseIcon from '../../../assets/svg/close.svg'
import SwitchCamIcon from '../../../assets/svg/switch-camera.svg'
import useModal from '../../../infrastructure/hooks/useModal';
import { SELECTED_MEDIA } from '../../../infrastructure/dtos/types/file';
import { MEDIA_HEIGHT, MEDIA_WIDTH } from '../../../infrastructure/dtos/constants';
import useWindowDimensions from '../../../infrastructure/hooks/useWindowDimensions';

const MAX_RECORDING_SECONDS = 10;

const ReactModalAny = ReactModal as any;

// FIX (medium): moved outside the component — user agent never changes,
// no reason to recompute this on every render.
const isiOS = /iPhone|iPad|iPod|Mac/i.test(navigator.userAgent);

export default function Camera({close, open = true, ...props}: {
  open: boolean;
  close?: () => void;
  onMediaSet: (medias: SELECTED_MEDIA[]) => void
}) {
  const windowDimension = useWindowDimensions();
  const webcamRef = useRef<Webcam>(null);
  const [fileInstance, setFileInstance] = useState<Blob>();
  const mediaRecorderRef = useRef<MediaRecorder>(null);
  const recordedChunks = useRef<Blob[]>([]);
  const [preview, setPreview] = useState('');
  const previewModal = useModal();

  const timerRef = useRef<NodeJS.Timeout>(null);
  const maxRecordingTimerRef = useRef<NodeJS.Timeout>(null);
  const [isLongPressed, setIsLongPressed] = useState(false);

  // FIX (high): mirror isLongPressed in a ref to avoid stale closure reads
  // in endPress, where reading the React state would return the previous render's value.
  const isLongPressedRef = useRef(false);

  const [camType, setCamType] = useState<'user' | 'environment'>('user');
  const actionOccurred = useRef(false);

  const width = windowDimension.isMobile ? windowDimension.windowDimensions.width : MEDIA_WIDTH;
  const height = windowDimension.isMobile ? windowDimension.windowDimensions.height : MEDIA_HEIGHT;

  // FIX (medium): memoize so it only recomputes when width/height change.
  const videoConstraints = useMemo(() => {
    if (isiOS) {
      return {
        width: { ideal: width },
        height: { ideal: height },
        aspectRatio: 16 / 9,
        advanced: [{ aspectRatio: 16 / 9, width, height }],
        facingMode: camType,
      };
    }
    return {
      width: { ideal: height },
      height: { ideal: width },
      aspectRatio: 9 / 16,
      facingMode: camType,
    };
  }, [width, height, camType]);

  // FIX (high): revoke object URLs on unmount and whenever preview changes to a
  // new URL, so blob memory is always freed even if the user closes without confirming.
  useEffect(() => {
    return () => {
      if (preview) {
        try {
          window.URL.revokeObjectURL(preview);
        } catch {
          // safe to ignore — preview may be a data URL, not an object URL
        }
      }
    };
  }, [preview]);

  // FIX (high): stop and clean up MediaRecorder on unmount so the stream is
  // released and the dataavailable listener doesn't fire after the component is gone.
  useEffect(() => {
    return () => {
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        mediaRecorderRef.current.stop();
      }
      clearTimeout(maxRecordingTimerRef.current ?? 0);
    };
  }, []);

  const clearVideo = () => {
    try {
      window.URL.revokeObjectURL(preview);
      setPreview('');
    } catch (error) {
      console.error('clearVideo', error);
    }
  };

  const closeCamera = () => {
    clearVideo();
    close?.();
  };

  // FIX (medium): added webcamRef and setFileInstance to the dependency array
  // so the memoized function always closes over the correct references.
  const onCapture = useCallback(async () => {
    const result = webcamRef.current?.getScreenshot();
    if (result) {
      const blob = await fetch(result).then(res => res.blob());
      setFileInstance(blob);
    }
    setPreview(result ?? '');
    previewModal.toogle(true);
  }, [previewModal, webcamRef, setFileInstance]);

  const stopRecording = useCallback(() => {
    clearTimeout(maxRecordingTimerRef.current ?? 0);

    if (
      mediaRecorderRef.current &&
      mediaRecorderRef.current.state !== 'inactive'
    ) {
      mediaRecorderRef.current.stop();
    }

    // Give the browser a tick to flush the final dataavailable chunk.
    setTimeout(() => {
      const chunks = recordedChunks.current;

      if (chunks.length === 0) {
        console.warn('No recorded chunks — nothing to save.');
        setIsLongPressed(false);
        isLongPressedRef.current = false;
        return;
      }

      const blob = new Blob(chunks, { type: 'video/mp4' });
      const url = URL.createObjectURL(blob);
      setPreview(url);
      setFileInstance(blob);
      recordedChunks.current = [];

      setIsLongPressed(false);
      isLongPressedRef.current = false;
    }, 1000);
  }, []);

  const handleLongPress = useCallback(() => {
    setIsLongPressed(true);
    isLongPressedRef.current = true;

    if (window.navigator.vibrate) window.navigator.vibrate(50);

    if (webcamRef.current?.stream) {
      mediaRecorderRef.current = new MediaRecorder(webcamRef.current.stream, {
        mimeType: 'video/mp4',
      });

      mediaRecorderRef.current.addEventListener('dataavailable', ({ data }) => {
        if (data.size > 0) {
          recordedChunks.current.push(data);
        }
      });

      mediaRecorderRef.current.start();

      // NEW: auto-stop after MAX_RECORDING_SECONDS and finalize the blob.
      maxRecordingTimerRef.current = setTimeout(() => {
        if (window.navigator.vibrate) window.navigator.vibrate([50, 50, 50]);
        stopRecording();
      }, MAX_RECORDING_SECONDS * 1000);
    }
  }, [stopRecording]);

  const startPress = () => {
    actionOccurred.current = false;
    timerRef.current = setTimeout(() => {
      handleLongPress();
      actionOccurred.current = true;
    }, 600);
  };

  const endPress = () => {
    clearTimeout(timerRef.current ?? 0);

    if (!actionOccurred.current) {
      onCapture();
      return;
    }

    // FIX (high): read from ref, not state — state may still be the previous
    // render's value inside this synchronous event handler.
    if (isLongPressedRef.current) {
      stopRecording();
    }
  };

  const cancelPress = () => {
    clearTimeout(timerRef.current ?? 0);
  };

  const toogleCam = () => {
    setCamType(cam => (cam === 'user' ? 'environment' : 'user'));
  };

  const setMedia = () => {
    props.onMediaSet([
      {
        src: preview,
        isImage: !(preview && preview.includes('http')),
        width,
        quality: width / height,
        file: fileInstance as File,
      },
    ]);
  };

  return (
    <ReactModalAny
      isOpen={open}
      onRequestClose={closeCamera}
      style={{
        content: {
          top: '50%',
          left: '50%',
          right: 'auto',
          bottom: 'auto',
          marginRight: '-50%',
          transform: 'translate(-50%, -50%)',
          background: '#000',
          padding: '0px',
          borderRadius: '10px',
        },
      }}
    >
      <div className={styles.container}>
        <Webcam
          audio={!preview}
          muted={true}
          ref={webcamRef}
          capture={camType}
          forceScreenshotSourceSize={true}
          screenshotFormat="image/jpeg"
          width={width}
          minScreenshotHeight={height}
          minScreenshotWidth={width}
          imageSmoothing={true}
          height={height}
          onError={() => {
            alert('Camera error — please check permissions.');
          }}
          disablePictureInPicture
          videoConstraints={videoConstraints}
        />

        {isLongPressed && (
          <div
            className={styles.recordingContainer}
            style={{ position: 'absolute', top: 20, left: 20 }}
          >
            <div className={styles.dot} />
            <span className={styles.recordingText}>
              Recording (max {MAX_RECORDING_SECONDS}s)
            </span>
          </div>
        )}

        {!preview && (
          <>
            {windowDimension.isMobile && (
              <img
                src={SwitchCamIcon as never}
                className={styles.switchCamCta}
                alt="switch-icon"
                onClick={toogleCam}
                width={50}
              />
            )}
            <div
              onTouchStart={startPress}
              onTouchEnd={endPress}
              onTouchMove={cancelPress}
              onMouseDown={startPress}
              onMouseUp={endPress}
              onTouchEndCapture={endPress}
              className={`${styles.camCta} ${isLongPressed ? styles.longActive : ''}`}
            />
            <img
              src={CloseIcon as never}
              className={styles.closeCamCta}
              alt="close-icon"
              onClick={closeCamera}
              width={50}
            />
          </>
        )}

        {preview && (
          <div className={styles.preview}>
            <div>
              <label>PREVIEW</label>

              {preview.includes('blob:') && (
                <video loop playsInline autoPlay key={preview} width={width} height={height}>
                  <source src={preview} type="video/mp4" />
                </video>
              )}

              {!preview.includes('blob:') && (
                <img
                  src={preview}
                  alt="preview"
                  width={width}
                  height={height}
                  style={{ objectFit: 'contain' }}
                />
              )}

              <div className="controls">
                <img src={CheckIcon as never} alt="check-icon" width={50} onClick={setMedia} />
                <img src={CloseIcon as never} alt="close-icon" onClick={clearVideo} width={50} />
              </div>
            </div>
          </div>
        )}
      </div>
    </ReactModalAny>
  );
}
